import axios from 'axios';
import { BYPASS_AUTH } from '../config/development';
import { isWorkOSEnabled, getWorkOSAuthenticated, getAccessToken, getAuthHeaders } from './workosAuth';

interface User {
  id: string;
  username: string;
  email: string;
  fullName: string;
  role: string;
  organizationId: string;
  organizationName: string;
  // Optional additional properties
  name?: string;
  profile_image?: string;
  first_name?: string;
  last_name?: string;
}

import { API_BASE_URL } from '../config';

const API_URL = API_BASE_URL;

// Dev-only user when BYPASS_AUTH is true (development). Production always uses AuthKit/WorkOS.
const DEV_USER: User = {
  id: 'dev-user',
  username: 'developer',
  email: 'dev@example.com',
  fullName: 'Development User',
  role: 'admin',
  organizationId: 'dev-org',
  organizationName: 'Development Organization'
};

/**
 * Check if user is authenticated. Production: WorkOS only (getWorkOSAuthenticated). Dev: BYPASS_AUTH or legacy token.
 */
export const isAuthenticated = (): boolean => {
  if (BYPASS_AUTH) {
    return true;
  }
  if (isWorkOSEnabled()) {
    return getWorkOSAuthenticated();
  }
  if (typeof import.meta !== 'undefined' && import.meta.env.DEV) {
    return !!localStorage.getItem('token');
  }
  return false;
};

export const login = async (_email: string, _password: string): Promise<User> => {
  if (isWorkOSEnabled()) {
    throw new Error('Use WorkOS sign-in; login() is only for dev when WorkOS is not configured.');
  }
  if (BYPASS_AUTH) {
    localStorage.setItem('token', 'dev-token');
    return DEV_USER;
  }
  // WorkOS-only: redirect to backend login (which redirects to WorkOS or dev bypass)
  if (typeof window !== 'undefined') {
    window.location.href = `${API_URL}/auth/login`;
  }
  throw new Error('Redirecting to sign-in');
};

export const register = async (
  username: string,
  email: string,
  password: string,
  fullName: string,
  role: string,
  organizationName: string,
  organizationType: string,
  organizationAction: string
): Promise<User> => {
  if (isWorkOSEnabled()) {
    throw new Error('Access by invitation only; register() is only for dev when WorkOS is not configured.');
  }
  if (BYPASS_AUTH) {
    localStorage.setItem('token', 'dev-token');
    
    // Create a mock user with the provided data
    const mockUser: User = {
      id: '1',
      username,
      email,
      fullName,
      role,
      organizationId: 'mock-org-id',
      organizationName: organizationName
    };
    
    return mockUser;
  }
  
  try {
    const response = await axios.post(`${API_URL}/auth/register`, { 
      username, 
      email, 
      password,
      full_name: fullName,
      role,
      organization_name: organizationName,
      organization_type: organizationType,
      organization_action: organizationAction
    });
    const { user, token } = response.data;
    localStorage.setItem('token', token);
    return user;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || 'Registration failed');
    }
    throw new Error('Registration failed');
  }
};

export const logout = async (): Promise<void> => {
  if (isWorkOSEnabled()) {
    return; // signOut is handled by UserContext via auth.signOut()
  }
  if (BYPASS_AUTH) {
    localStorage.removeItem('token');
    return;
  }
  try {
    await axios.post(`${API_URL}/auth/logout`);
    localStorage.removeItem('token');
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || 'Logout failed');
    }
    throw new Error('Logout failed');
  }
};

export const getCurrentUser = async (): Promise<User> => {
  if (BYPASS_AUTH) {
    return DEV_USER;
  }
  const mapMeToUser = (raw: Record<string, unknown>) => ({
    id: String(raw.id ?? ''),
    username: (raw.username as string) ?? (typeof raw.email === 'string' ? raw.email.split('@')[0] : '') ?? 'user',
    email: String(raw.email ?? ''),
    fullName: (raw.fullName as string) ?? (raw.full_name as string) ?? [raw.firstName, raw.lastName].filter(Boolean).join(' ') ?? '',
    role: (raw.role as string) ?? 'user',
    organizationId: String(raw.organizationId ?? raw.orgId ?? ''),
    organizationName: String(raw.organizationName ?? raw.orgName ?? ''),
  } as User);

  // WorkOS / production: single request with Bearer (from getAuthHeaders) and cookie (credentials: 'include')
  try {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_URL}/auth/me`, { credentials: 'include', headers });
    if (res.ok) {
      const data = await res.json();
      const raw = data.user ?? data;
      return mapMeToUser(raw);
    }
  } catch {
    // fall through
  }

  // Fallback: Bearer token (WorkOS getter/sessionStorage or dev-only localStorage)
  try {
    const token = await getAccessToken();
    if (!token && typeof import.meta !== 'undefined' && import.meta.env.DEV) {
      const devToken = localStorage.getItem('token');
      if (devToken) {
        const response = await axios.get(`${API_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${devToken}` },
          withCredentials: true,
        });
        const data = response.data;
        const raw = data.user ?? data;
        return mapMeToUser(raw);
      }
    }
    if (!token) {
      throw new Error('No token found');
    }
    const response = await axios.get(`${API_URL}/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
      withCredentials: true,
    });
    const data = response.data;
    const raw = data.user ?? data;
    return mapMeToUser(raw);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || 'Failed to get current user');
    }
    throw new Error('Failed to get current user');
  }
};

export const updateProfile = async (data: Partial<User>): Promise<User> => {
  try {
    const token = await getAccessToken();
    if (!token && typeof import.meta !== 'undefined' && import.meta.env.DEV) {
      const devToken = localStorage.getItem('token');
      if (devToken) {
        const response = await axios.patch(`${API_URL}/auth/profile`, data, {
          headers: { Authorization: `Bearer ${devToken}` },
        });
        return response.data.user;
      }
    }
    if (!token) {
      throw new Error('No token found');
    }
    const response = await axios.patch(`${API_URL}/auth/profile`, data, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data.user;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || 'Failed to update profile');
    }
    throw error;
  }
};

/**
 * Change user password
 * @param oldPassword Current password
 * @param newPassword New password
 * @returns Promise that resolves when password is changed
 */
export const changePassword = async (oldPassword: string, newPassword: string): Promise<void> => {
  try {
    await axios.post(`${API_URL}/auth/change-password`, {
      old_password: oldPassword,
      new_password: newPassword
    });
  } catch (error: any) {
    console.error('Password change failed:', error);
    
    if (error.response) {
      switch (error.response.status) {
        case 400:
          throw new Error('Password does not meet requirements');
        case 401:
          throw new Error('Current password is incorrect');
        default:
          throw new Error(error.response.data?.message || 'Failed to change password');
      }
    }
    
    throw new Error('Network error. Please check your connection');
  }
};

/**
 * Request password reset
 * @param email User's email address
 * @returns Promise that resolves when reset email is sent
 */
export const requestPasswordReset = async (email: string): Promise<void> => {
  try {
    await axios.post(`${API_URL}/auth/forgot-password`, { email });
  } catch (error: any) {
    console.error('Password reset request failed:', error);
    
    if (error.response && error.response.status !== 404) {
      // Don't expose if email exists or not (for security)
      throw new Error('An error occurred. Please try again later');
    }
    
    throw new Error('Network error. Please check your connection');
  }
};

/**
 * Reset password with token
 * @param token Reset token from email
 * @param newPassword New password
 * @returns Promise that resolves when password is reset
 */
export const resetPassword = async (token: string, newPassword: string): Promise<void> => {
  try {
    await axios.post(`${API_URL}/auth/reset-password`, {
      token,
      new_password: newPassword
    });
  } catch (error: any) {
    console.error('Password reset failed:', error);
    
    if (error.response) {
      switch (error.response.status) {
        case 400:
          throw new Error('Password does not meet requirements');
        case 401:
          throw new Error('Invalid or expired reset token');
        default:
          throw new Error(error.response.data?.message || 'Failed to reset password');
      }
    }
    
    throw new Error('Network error. Please check your connection');
  }
};

/**
 * Verify email with token
 * @param token Verification token from email
 * @returns Promise that resolves when email is verified
 */
export const verifyEmail = async (token: string): Promise<void> => {
  try {
    await axios.post(`${API_URL}/auth/verify-email`, { token });
  } catch (error: any) {
    console.error('Email verification failed:', error);
    
    if (error.response) {
      switch (error.response.status) {
        case 401:
          throw new Error('Invalid or expired verification token');
        default:
          throw new Error(error.response.data?.message || 'Failed to verify email');
      }
    }
    
    throw new Error('Network error. Please check your connection');
  }
};

/**
 * Refresh the authentication token
 * @returns Promise with new auth response
 */
export const refreshToken = async (): Promise<User> => {
  if (isWorkOSEnabled()) {
    throw new Error('WorkOS manages tokens; refreshToken is only for dev when WorkOS is not configured.');
  }
  if (typeof import.meta !== 'undefined' && import.meta.env.PROD) {
    throw new Error('WorkOS is the only auth method in production.');
  }
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No token found');
    }

    const response = await axios.post(`${API_URL}/auth/refresh-token`, null, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    const { user, token: newToken } = response.data;
    localStorage.setItem('token', newToken);
    return user;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || 'Token refresh failed');
    }
    throw new Error('Token refresh failed');
  }
}; 